import { ChangeEvent, useEffect, useState } from 'react';
import Link from 'next/link';
import { Box, Typography } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { useRouter } from 'next/router';

import theme from 'src/style-system/theme';
import { useResetPasswordMutation } from 'src/http/resetPassword';
import { snackbarInfo } from 'store/utils';

import { validateEmail } from '@utils/validator';
import InputError from '@components/InputError';
import { Form } from '@components/UseForm';
import Controls from '@components/Controls';
import CuminButton from '@components/Button';
import PageLoaderModal from '@components/PageLoaderModal';
import { AppRoutes } from '@utils/appRoutes';
import useAuth from '@utils/useAuth';
import TCCLogo from '@public/assets/svg/TCCLogo.svg';

import { useStyles } from './useStyles';

const ForgotPassword = () => {
  const [errors, setErrors] = useState<string>('');
  const [input, setInput] = useState<string>('');
  const [resetLinkSend, setResetLinkSend] = useState<boolean>(false);

  const classes = useStyles();

  const router = useRouter();
  const { detectedCountryCode: countryCode } = useAuth();

  const resetForm = () => {
    setInput('');
    setErrors('');
  };

  const { resetPassword, resetPasswordLoading } = useResetPasswordMutation();

  const validateForm: () => boolean = () => {
    let valid = true;

    const emailValidation = validateEmail(input.trim());

    if (!emailValidation.valid) {
      valid = false;
      setErrors(emailValidation?.messageKey || '');
    }
    return valid;
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    if (errors) {
      setErrors('');
    }
    setInput(value);
  };

  const handleSignIn = async () => {
    if (validateForm()) {
      // reset password mutation here
      try {
        const res = await resetPassword({
          variables: {
            input: {
              email: input.trim(),
            },
          },
        });
        resetForm();
        setResetLinkSend(true);
      } catch (error: any) {
        snackbarInfo({
          message: error?.message,
          type: 'error',
        });
      }
    }
  };

  //to start reset password in on hit of Enter from keyboard
  useEffect(() => {
    const listener = (event: any) => {
      if (event.code === 'Enter') {
        handleSignIn();
      }
    };
    document.addEventListener('keydown', listener);
    return () => {
      document.removeEventListener('keydown', listener);
    };
  }, [handleSignIn]);

  const onClickErrorCloseIcon = (name: string) => {
    setErrors('');
  };

  const onClickBackArrow = () => {
    router.back();
  };

  const handleGoToHomePage = () => {
    router.push(AppRoutes.HOME_ROUTE);
  };

  return (
    <>
      {resetPasswordLoading ? <PageLoaderModal /> : null}
      <Box className={classes.mainContainer}>
        <Box className={classes.leftSectionContainer} />
        <Box className={classes.rightSectionContainer}>
          <Box className={classes.headerContainer}>
            <ArrowBackIosIcon
              fontSize="large"
              sx={{
                color: 'cinnamon.main',
                cursor: 'pointer',
              }}
              onClick={onClickBackArrow}
            />

            <Link href={AppRoutes.HOME_ROUTE}>
              <a>
                <TCCLogo />
              </a>
            </Link>
          </Box>
          {resetLinkSend ? (
            <Box className={classes.sendLinkInstruction}>
              <Typography
                variant="body1"
                align="center"
                color="cumin.main"
                maxWidth="450px"
              >
                If you have an active account with us, your reset link will have
                been sent to your inbox! Please follow the instructions provided
                to reset your password.
              </Typography>
              <Box className={classes.goToHomePageBtnWrapper}>
                <CuminButton
                  color="turmeric"
                  variant="contained"
                  textColor="garamMasala.main"
                  onClick={handleGoToHomePage}
                  className={classes.desktopLoginBtn}
                >
                  Go to Homepage
                </CuminButton>
              </Box>
            </Box>
          ) : (
            <Box className={classes.wrapper}>
              <Box className={classes.upperWrapper}>
                <Typography
                  variant="h2"
                  align="center"
                  color="cinnamon.main"
                  className={classes.loginTitle}
                >
                  Forgot Password
                </Typography>
                <Typography variant="body2" align="center" color="cumin.main">
                  Enter your email address you used to join the club and we'll
                  send uou instructions to reset your password!
                </Typography>
              </Box>
              <Box className={classes.formContainer}>
                <Form onSubmit={handleSignIn}>
                  <Box className={classes.formItem}>
                    <Typography
                      variant="body1"
                      align="left"
                      color="cumin.main"
                      ml={{ xs: 3, md: 0 }}
                      mb={{ xs: 1, md: 0 }}
                    >
                      Email
                    </Typography>
                    <Box sx={{ position: 'relative' }}>
                      <Controls.InputField
                        name="email"
                        placeholder="Email Address"
                        value={input}
                        onChange={handleInputChange}
                        className={classes.input}
                      />
                      <InputError
                        error={errors}
                        className={classes.emailError}
                        onClickErrorCloseIcon={() =>
                          onClickErrorCloseIcon('email')
                        }
                      />
                    </Box>
                  </Box>
                </Form>

                <Box className={classes.loginBtnWrapper}>
                  <CuminButton
                    color="turmeric"
                    variant="contained"
                    textColor="garamMasala.main"
                    borderRadius="55"
                    height="76"
                    onClick={handleSignIn}
                    className={classes.desktopLoginBtn}
                  >
                    Send Me Reset Link
                  </CuminButton>
                </Box>
              </Box>

              <Box className={classes.instructions}>
                {/* <Typography
                  variant="body2"
                  align="center"
                  color="cinnamon.main"
                  sx={{
                    cursor: 'pointer',
                    borderBottom: `2px solid ${theme.palette.cinnamon.main}`,
                  }}
                >
                  Didn't receive your instructions?
                </Typography> */}
                <CuminButton
                  color="cinnamon"
                  variant="contained"
                  textColor="whitePepper.main"
                  borderRadius="30"
                  width="250px"
                  height="48"
                  className={classes.loginBtn}
                  onClick={handleSignIn}
                >
                  Send Me Reset Link
                </CuminButton>
              </Box>

              <Box className={classes.registeredSubscription}>
                <Typography variant="body2" align="center" color="cumin.main">
                  Haven't ordered yet?
                </Typography>
                <Link href={`/${countryCode}${AppRoutes.ORDER_STEP_ONE}`}>
                  <Typography
                    variant="body2"
                    align="center"
                    color="cinnamon.main"
                    sx={{ cursor: 'pointer' }}
                  >
                    Order Now!
                  </Typography>
                </Link>
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default ForgotPassword;
